/*
 * Copyright (c) 2024 Bolmate B.V.
 */

import {AwesomeIcon, Loader, MenuPlacement, OptionsMenu, OptionsMenuItem} from '@dvrd/dvr-controls';

import React, {MouseEventHandler} from 'react';

import {useTranslation} from 'react-i18next';

import AccountUser from '../../../models/accountUserModel';

import Invitation from '../../../models/invitationModel';

import {BolmateButton} from '../../controls';

import {translateUserRole} from '../../../util/accessUtil';

import CommonPagination from '../../misc/commonPagination';

import {AccessKey} from '../../../util/interfaces';

import {DUTCH_DATE_FORMAT} from '../../../util/constants';

import {Icon} from '@iconify/react';

import optionsHorizontal from '@iconify/icons-mi/options-horizontal';

import classNames from 'classnames';

import useRight from '../../misc/useRight';

interface SharedProps {
    onChangePage: (page: number) => MouseEventHandler;
    loading: boolean;
    page: number;
    limit: number;
    total: number;
}

interface UsersProps extends SharedProps {
    onClickUser: (user: AccountUser) => MouseEventHandler;
    onClickRevoke: (user: AccountUser) => MouseEventHandler;
    accountUsers: Array<AccountUser>;
}

interface InvitationsProps extends SharedProps {
    onClickInvite: MouseEventHandler;
    onDeleteInvitation: (invitation: Invitation) => MouseEventHandler;
    onSendInvitation: (invitation: Invitation) => MouseEventHandler;
    invitations: Array<Invitation>;
}

export function AccountUsers(props: UsersProps) {
    const {t} = useTranslation();
    const [hasEditRight] = useRight(AccessKey.ACCOUNT_USERS_EDIT);
    const {limit, onChangePage, page, loading, onClickUser, onClickRevoke, total, accountUsers} = props;

    function renderAccountUser(accountUser: AccountUser, index: number) {
        const isOwner = accountUser.isOwner();
        const renderDelete = !isOwner && hasEditRight;
        const onClick = !isOwner && hasEditRight ? onClickUser(accountUser) : undefined;
        return (
            <div key={index} className={classNames('row default-table-row', isOwner && 'owner')} onClick={onClick}>
                <label>{accountUser.fullName}</label>
                <label>{accountUser.email}</label>
                <label>{translateUserRole(accountUser.userRole)}</label>
                <label>{accountUser.createdAtMoment.format(DUTCH_DATE_FORMAT)}</label>
                <label>{accountUser.updatedAtMoment?.format(DUTCH_DATE_FORMAT) ?? '-'}</label>
                <label>{accountUser.lastLoginDate?.format(DUTCH_DATE_FORMAT + ' HH:mm') ?? '-'}</label>
                {renderDelete && (
                    <AwesomeIcon name='trash-alt' onClick={onClickRevoke(accountUser)} className='action-btn delete' />
                )}
            </div>
        );
    }

    return (
        <div className='account-users-container'>
            <Loader active={loading} />
            <div className='part-header'>
                <label className='part-title'>{t('settings.accountUsers.usersTitle', 'Gebruikers')}</label>
            </div>
            <div>
                <div className='row default-table-row head'>
                    <label>{t('settings.accountUsers.colName', 'NAAM')}</label>
                    <label>{t('settings.accountUsers.colEmail', 'E-MAIL')}</label>
                    <label>{t('settings.accountUsers.colType', 'TYPE')}</label>
                    <label>{t('settings.accountUsers.colAccessSince', 'TOEGANG SINDS')}</label>
                    <label>{t('settings.accountUsers.colLastModified', 'LAATST GEWIJZIGD')}</label>
                    <label>{t('settings.accountUsers.colLastLogin', 'LAATST INGELOGD')}</label>
                    <div />
                </div>
                {accountUsers.map(renderAccountUser)}
                {!accountUsers.length && (
                    <div className='default-table-row empty'>
                        <label className='empty-label'>
                            {t('settings.accountUsers.noUsers', '- Geen gebruikers gevonden -')}
                        </label>
                    </div>
                )}
            </div>
            <CommonPagination onChangePage={onChangePage} page={page} total={total} limit={limit} />
        </div>
    );
}

export function AccountInvitations(props: InvitationsProps) {
    const {t} = useTranslation();
    const [canInvite] = useRight(AccessKey.ACCOUNT_USERS_INVITE);
    const {
        onClickInvite,
        onDeleteInvitation,
        onSendInvitation,
        page,
        loading,
        onChangePage,
        total,
        limit,
        invitations
    } = props;

    function getMenuOptions(invitation: Invitation): Array<OptionsMenuItem> {
        if (!canInvite) return [];
        return [
            {
                label: t('settings.accountUsers.resend', 'Opnieuw verzenden'),
                icon: <AwesomeIcon name='paper-plane' className='icon' />,
                onClick: onSendInvitation(invitation)
            },
            {
                label: t('settings.accountUsers.revoke', 'Intrekken'),
                icon: <AwesomeIcon name='trash-alt' className='icon' />,
                onClick: onDeleteInvitation(invitation)
            }
        ];
    }

    function renderInvitation(invitation: Invitation, index: number) {
        const optionsRef = React.createRef<OptionsMenu>();

        function onClickRow() {
            optionsRef.current?.onOpenExternal();
        }

        return (
            <div key={index} className='row default-table-row' onClick={onClickRow}>
                <label>{invitation.createdAtMoment.format(DUTCH_DATE_FORMAT)}</label>
                <label>{invitation.email}</label>
                <label>{translateUserRole(invitation.userRole)}</label>
                <label>{invitation.invitedByUser?.fullName ?? '-'}</label>
                <label>{invitation.lastSentAtMoment?.format(DUTCH_DATE_FORMAT) ?? '-'}</label>
                {canInvite ? (
                    <OptionsMenu
                        options={getMenuOptions(invitation)}
                        placement={MenuPlacement.BOTTOM_LEFT}
                        optionsComponent={<Icon icon={optionsHorizontal} className='options-icon' />}
                        containerClass='common-options'
                        ref={optionsRef}
                    />
                ) : (
                    <div />
                )}
            </div>
        );
    }

    return (
        <div className='invitations-container'>
            <Loader active={loading} />
            <div className='part-header'>
                <label className='part-title'>{t('settings.accountUsers.invitationsTitle', 'Uitnodigingen')}</label>
                {canInvite && (
                    <BolmateButton
                        onClick={onClickInvite}
                        label={t('settings.accountUsers.inviteUser', '+ Gebruiker uitnodigen')}
                        disabled={loading}
                        secondary
                    />
                )}
            </div>
            <div>
                <div className='row default-table-row head'>
                    <label>{t('settings.accountUsers.colInvitedOn', 'UITGENODIGD OP')}</label>
                    <label>{t('settings.accountUsers.colEmail', 'E-MAIL')}</label>
                    <label>{t('settings.accountUsers.colType', 'TYPE')}</label>
                    <label>{t('settings.accountUsers.colInvitedBy', 'UITGENODIGD DOOR')}</label>
                    <label>{t('settings.accountUsers.colLastSent', 'LAATST VERZONDEN')}</label>
                    <div />
                </div>
                {invitations.map(renderInvitation)}
                {!invitations.length && (
                    <div className='default-table-row empty'>
                        <label className='empty-label'>
                            {t('settings.accountUsers.noInvitations', '- Er zijn geen uitnodigingen gevonden -')}
                        </label>
                    </div>
                )}
            </div>
            <CommonPagination onChangePage={onChangePage} page={page} total={total} limit={limit} />
        </div>
    );
}
